import React, {useContext, useEffect, useMemo, useState} from 'react';
import Table from "@amzn/awsui-components-react/polaris/table";
import YtdPeriodBalance from 'src/models/periodCharges/YtdPeriodBalance';
import {Box, Button, Header, SpaceBetween, TextContent} from '@amzn/awsui-components-react';
import ArrayUtils from 'src/utils/arrayUtils';
import BalanceAdjustmentModal from './BalanceAdjustmentModal';
import {PeriodChargesContext} from './PeriodChargesView';
import {getPermissions} from '../AppPermissions';
import {AppModules} from 'src/models/permissions/RolePermissions';
import {CLI} from 'src/models/common/CLI';
import CONSTANTS from 'src/utils/constants';
import {GlobalAppContext} from '../App';
import {MarkCompletePayload} from 'src/models/dashboard/Task';

export default function BottomYtdBalancesGrid(props: {
    data: YtdPeriodBalance[],
    cli?: CLI,
    selectedJeTagId?: number,
    onAdjustBalanceSubmitted: (adjustmentBaseAmount:number, adjustmentTotalAmount: number, calculationNumber: string, jeTagId?: number) => void;
}) {
    const {state, services} = useContext(PeriodChargesContext);
    const globalContext = useContext(GlobalAppContext);
    const {balanceAdjustmentIsSaving, balanceAdjustmentError, saveAdjustmentResponse} = state;
    const {data, cli, selectedJeTagId, onAdjustBalanceSubmitted} = props;
    const year = ArrayUtils.isNullOrEmpty(data) ? '' : data[0].year;
    const [adjustmentModalVisible, setAdjustmentModalVisible] = useState(false);
    const [jeTagDescription, setJeTagDescription] = useState('');
    const {canEdit} = getPermissions(AppModules.PERIOD_CHARGES);
    const cliHasBeatQualifier = CONSTANTS.BEAT_QUALIFIERS.includes(cli?.beat || "");
    
    // Mark as complete functionality
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [taskIds, setTaskIds] = useState<string[]>([]);
    const [markCompletePayload, setMarkCompletePayload] = useState<MarkCompletePayload | null>(null);
    
    const [taskCheckResult, taskCheckLoading, taskCheckError] = globalContext.services.dashboardService.getBeginningBalanceTasks(cli?.calculationNumber || null, refreshCounter);
    const [markCompleteResult, markingComplete, markCompleteError] = globalContext.services.dashboardService.markNotificationsComplete(markCompletePayload);

    const columnDefinitions = useMemo(() => {
        const columns = [
            {
                id: "month",
                header: "Month",
                cell: (e: YtdPeriodBalance) => e.month,
                width: 80,
                minWidth: 80,
            },
            {
                id: "adjustment",
                header: "Monthly Adjustment",
                cell: (e: YtdPeriodBalance) => <Box
                    float="right">{e.totalAmount == null ? '-' : services.formattingService.formatNumber(e.totalAmount, true, 2)}</Box>,
                width: 200,
                minWidth: 110,
            },
            {
                id: "ytdTotalBeginningBalance",
                header: cliHasBeatQualifier ? "YTD total beginning balance" : "YTD beginning balance",
                cell: (e: YtdPeriodBalance) => <Box
                    float="right">{e.ytdTotalBeginningBalance == null ? '-' : services.formattingService.formatNumber(e.ytdTotalBeginningBalance, true, 2)}</Box>,
                width: cliHasBeatQualifier ? 260 : 240,
                minWidth: 200
            },
            {
                id: "source",
                header: "Source",
                cell: (e: YtdPeriodBalance) => e.source,
                width: 250,
                minWidth: 150
            },
            {
                id: "createUser",
                header: "Updated by",
                cell: (e: YtdPeriodBalance) => e.createUser || "-",
                width: 150,
                minWidth: 150
            },
            {
                id: "updatedOn",
                header: "Last update timestamp",
                cell: (e: YtdPeriodBalance) => e.updatedOn || "-",
                width: 200,
                minWidth: 170
            }
        ];
        if (cliHasBeatQualifier) {
            columns.splice(2, 0, {
                id: "ytdBaseBeginningBalance",
                header: "YTD base beginning balance",
                cell: (e: YtdPeriodBalance) => <Box
                    float="right">{e.ytdBaseBeginningBalance == null ? '-' : services.formattingService.formatNumber(e.ytdBaseBeginningBalance, true, 2)}</Box>,
                width: 240,
                minWidth: 200,
            });
            columns.splice(2, 0, {
                id: "baseAmount",
                header: "Base amount",
                cell: (e: YtdPeriodBalance) => <Box
                    float="right">{e.baseAmount == null ? '-' : services.formattingService.formatNumber(e.baseAmount, true, 2)}</Box>,
                width: 180,
                minWidth: 110,
            });
        }
        return columns;
    }, [cliHasBeatQualifier]);

    const [jeTagsDetails, jeTagsLoading, jeTagsError] = services.calculationStepsService.getJETagsForYTDGrid(cli?.calculationNumber)
    
    // Reset task IDs when CLI changes
    useEffect(() => {
        if (!cli?.calculationNumber) {
            setTaskIds([]);
        }
    }, [cli?.calculationNumber]);
    
    // Update task IDs when task check result is received
    useEffect(() => {
        if (taskCheckResult != null) {
            setTaskIds(taskCheckResult.taskIds || []);
        }
    }, [taskCheckResult]);
    
    // Handle task check error
    useEffect(() => {
        if (taskCheckError) {
            globalContext.services.messageService.showErrorBanner(taskCheckError);
        }
    }, [taskCheckError]);
    
    // Handle mark complete result
    useEffect(() => {
        if (markCompleteResult != null) {
            globalContext.services.messageService.showSuccessAutoDismissBanner(`${markCompleteResult.numSuccessful} task(s) have been marked as complete.`);
            if (markCompleteResult.numFailed > 0) {
                let failedMessage = `${markCompleteResult.numFailed} task(s) failed to be marked as complete. `;
                markCompleteResult.errors?.forEach((x: string) => failedMessage = failedMessage + `${x} `);
                globalContext.services.messageService.showErrorBanner(failedMessage);
            }
            // Re-check tasks after marking complete
            setRefreshCounter(prev => prev + 1);
        }
    }, [markCompleteResult]);
    
    // Handle mark complete error
    useEffect(() => {
        if (markCompleteError) {
            globalContext.services.messageService.showErrorBanner(markCompleteError);
        }
    }, [markCompleteError]);
    
    useEffect(() => {
        if (saveAdjustmentResponse != null) {
            setAdjustmentModalVisible(false);
        }
    }, [saveAdjustmentResponse]);

    useEffect(() => {
        if (jeTagsError) {
            services.messageService.showErrorBanner(jeTagsError);
            return;
        }

        const hasData = data.length > 0;
        const hasJeTagId = hasData && data[0].jeTagId !== undefined;

        if (jeTagsDetails && hasJeTagId) {
            const jeTag = jeTagsDetails.JETags.find((tag) => tag.id === data[0].jeTagId);
            setJeTagDescription(jeTag?.description || '');
        } else {
            setJeTagDescription('');
        }
    }, [jeTagsDetails, jeTagsError, data]);
    
    const handleMarkAsComplete = () => {
        if (!cli?.calculationNumber || taskIds.length === 0) {
            return;
        }
        
        setMarkCompletePayload({ 
            notifications: taskIds.map(taskId => ({
                owner: globalContext.userProfile?.user || '',
                notificationId: taskId
            }))
        });
    };
    
    // Button should be disabled/loading when: no tasks, marking complete, or rechecking tasks
    const isMarkCompleteDisabled = taskIds.length === 0 || markingComplete || taskCheckLoading;
    const isMarkCompleteLoading = markingComplete || taskCheckLoading;

    return <>
        <SpaceBetween direction="vertical" size="s">
            {
                jeTagDescription.length > 0 ?
                    <TextContent>
                        <h4>JE Description</h4>
                        <p>{jeTagDescription}</p>
                    </TextContent> :
                    <></>
            }
            <Table
                columnDefinitions={columnDefinitions}
                items={data}
                loadingText="Loading resources"
                resizableColumns
                empty={
                    <Box
                        margin={{vertical: "xs"}}
                        textAlign="center"
                        color="inherit">
                        <SpaceBetween size="m">
                            <b>No resources</b>
                        </SpaceBetween>
                    </Box>
                }
                header={
                    <Header actions={canEdit ?
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button onClick={() => setAdjustmentModalVisible(true)}>
                                Adjust balance
                            </Button>
                            <Button
                                onClick={handleMarkAsComplete}
                                disabled={isMarkCompleteDisabled}
                                loading={isMarkCompleteLoading}
                            >
                                Mark as complete
                            </Button>
                        </SpaceBetween> : null}>{year} YTD
                        Prior Period Balance</Header>
                }
            ></Table>
            <BalanceAdjustmentModal
                cli={cli}
                jeTagId={selectedJeTagId}
                visible={adjustmentModalVisible}
                isSaving={balanceAdjustmentIsSaving}
                savingError={balanceAdjustmentError}
                onAdjustmentSubmitted={onAdjustBalanceSubmitted}
                onDismiss={() => setAdjustmentModalVisible(false)}/>
        </SpaceBetween>
    </>
}